import {
  placeholderChipSelectedStyle,
  placeholderChipStyle,
} from "src/lib/placeholders/placeholderChipStyle.ts";
import { css } from "@emotion/react";
import type { EditorCSSProps } from "./CKEditor5.types.ts";
import type { ExactTheme } from "src/lib/emotion/theme.ts";

export const editor =
  ({ height, minHeight, maxHeight, disabled, error }: EditorCSSProps) =>
  (theme: ExactTheme) => {
    const {
      colorsV2,
      borderRadiusV2,
      inputColorV2,
      inputWeightV2,
      inputV2Placeholder,
      inputV2PlaceholderDisabled,
      fontSizesV2,
    } = theme;

    return css({
      color: disabled ? inputColorV2.disabled : inputColorV2.normal,
      fontWeight: disabled ? inputWeightV2.disabled : inputWeightV2.normal,
      fontSize: fontSizesV2.body,
      "--ck-border-radius": borderRadiusV2.primary,
      "--ck-color-text": colorsV2.grey[800],
      "--ck-color-toolbar-background": colorsV2.grey[50],
      "--ck-color-base-border": colorsV2.grey[200],
      // "--ck-font-size-base":"14px",
      minWidth: 0,

      height: height,
      minHeight: minHeight,
      maxHeight: maxHeight,

      ".ck-placeholder": {
        ...(disabled ? inputV2PlaceholderDisabled : inputV2Placeholder),
      },

      // Make changes to this section with caution.
      "[role='application']": {
        display: "flex",
        flexDirection: "column",
        height: "auto !important",
        minHeight: minHeight || (height ? "100%" : undefined),
        maxHeight: maxHeight,

        ".ck-editor__top": {
          flex: 0,

          ".ck-toolbar:first-of-type": {
            borderBottomLeftRadius: "unset",
            borderBottomRightRadius: "unset",
          },
        },

        ".ck-editor__main": {
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flexBasis: maxHeight ? undefined : "54.8px",
          flexGrow: 1,

          overflowY: height || maxHeight ? "auto" : undefined,

          ".ck-editor__editable_inline:not(.ck-comment__input *)": {
            scrollbarWidth: "thin",
            flexGrow: 1,

            '&[contenteditable="false"]': {
              backgroundColor: colorsV2.grey[50],
            },
          },
        },
      },
      // After, be sure to check the component's behavior for responsiveness,
      // with each value separately: height, minHeight, maxHeight, minHeight-maxHeight.

      ".ck-content": {
        lineHeight: 1.6,
        wordBreak: "break-word",

        // Чип плейсхолдера. Виден как предмет, а не как текст: иначе
        // неделимость приходится обнаруживать курсором, а до тех пор она
        // выглядит как подвисший редактор.
        "span.ck-placeholder-chip": placeholderChipStyle(theme),

        "span.ck-placeholder-chip.ck-widget_selected":
          placeholderChipSelectedStyle(theme),

        border:
          error && !disabled
            ? `1px solid ${colorsV2.red[300]}!important`
            : undefined,

        "h3.category": {
          fontFamily: "Oswald",
          fontSize: 20,
          fontWeight: "bold",
          color: "#555",
          letterSpacing: 10,
          margin: 0,
          padding: 0,
        },

        "h2.document-title": {
          fontFamily: "Oswald",
          fontSize: 50,
          fontWeight: "bold",
          margin: 0,
          padding: 0,
          border: 0,
        },

        "h3.document-subtitle": {
          fontFamily: "Oswald",
          fontSize: 20,
          color: "#555",
          margin: "0 0 1em",
          fontWeight: "bold",
          padding: 0,
        },

        "p.info-box": {
          "--background-size": 30,
          "--background-color": "#e91e63",
          padding: "1.2em 2em",
          border: "1px solid var(--background-color)",
          background: `
          linear-gradient(
            135deg,
            var(--background-color) 0%,
            var(--background-color) var(--background-size),
            transparent var(--background-size)
          ),
          linear-gradient(
            135deg,
            transparent calc(100% - var(--background-size)),
            var(--background-color) calc(100% - var(--background-size)),
            var(--background-color)
          )
        `,
          borderRadius: 10,
          margin: "1.5em 2em",
          boxShadow: "5px 5px 0 #ffe6ef",
        },

        "blockquote.side-quote": {
          fontFamily: "Oswald",
          fontStyle: "normal",
          float: "right",
          width: "35%",
          position: "relative",
          border: 0,
          overflow: "visible",
          zIndex: 1,
          marginLeft: "1em",

          "&::before": {
            content: '"“"',
            position: "absolute",
            top: -37,
            left: -10,
            display: "block",
            fontSize: 200,
            color: "#e7e7e7",
            zIndex: -1,
            lineHeight: 1,
          },

          p: {
            fontSize: "2em",
            lineHeight: 1,
          },

          "p:last-child:not(:first-of-type)": {
            fontSize: "1.3em",
            textAlign: "right",
            color: "#555",
          },
        },

        "span.marker": {
          background: "yellow",
        },

        "span.spoiler": {
          background: "#000",
          color: "#000",
          "&:hover": {
            color: "#fff",
          },
        },

        "pre.fancy-code": {
          border: 0,
          marginLeft: "2em",
          marginRight: "2em",
          borderRadius: 10,

          "&::before": {
            content: '""',
            display: "block",
            height: 13,
            background: `url(data:image/svg+xml;base64,PHN2ZyBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1NCAxMyI+CiAgPGNpcmNsZSBjeD0iNi41IiBjeT0iNi41IiByPSI2LjUiIGZpbGw9IiNGMzZCNUMiLz4KICA8Y2lyY2xlIGN4PSIyNi41IiBjeT0iNi41IiByPSI2LjUiIGZpbGw9IiNGOUJFNEQiLz4KICA8Y2lyY2xlIGN4PSI0Ny41IiBjeT0iNi41IiByPSI2LjUiIGZpbGw9IiM1NkM0NTMiLz4KPC9zdmc+Cg==)`,
            marginBottom: 8,
            backgroundRepeat: "no-repeat",
          },
        },

        "pre.fancy-code-dark": {
          background: "#272822",
          color: "#fff",
          boxShadow: "5px 5px 0 #0000001f",
        },

        "pre.fancy-code-bright": {
          background: "#dddfe0",
          color: "#000",
          boxShadow: "5px 5px 0 #b3b3b3",
        },
      },
    });
  };

export const additionalElem = (
  placement: "topLeft" | "topRight" | "bottomLeft" | "bottomRight",
) =>
  css({
    position: "absolute",
    ...(placement === "topLeft" && { top: 0, left: 0 }),
    ...(placement === "topRight" && { top: 0, right: 0 }),
    ...(placement === "bottomLeft" && { bottom: 0, left: 0 }),
    ...(placement === "bottomRight" && { bottom: 0, right: 0 }),
  });
